import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import { Button } from '@codex-brightworld/basics';
import { Icon } from '@codex-brightworld/icons';
import { addToBasket } from '../../helpers/addToBasket';

const StyledIcon = styled(Icon)`
  @font-face {
    font-family: 'CurrysSans';
    src: url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.woff') format('woff'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.otf') format('truetype'),
        url('https://currys-ssl.cdn.dixons.com/css/themes/fonts/currys/CurrysSans-Regular.eot?') format('embedded-opentype');
  }

  width: 24px;
  height: 24px;
  margin: -8px 10px -5px 0px;
`;

const InProgressButton = styled(Button)`
  background: #fff;
  font-family: CurrysSans;
  height: 44px;
  width: 100%;
  padding-right: 25px;
  padding-left: 25px;
  
  span svg path:last-child {
    stroke: none;
    fill: ${(props) => props.theme.colors.neutral.grey[2]};
  }
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    max-width: 215px;
  }
`;

const AddedButton = styled(Button)`
  width: 100%;
  padding-right: 0;
  padding-left: 0;
  color: ${(props) => props.theme.colors.neutral.grey[3]};
  border: 2px solid ${(props) => props.theme.colors.neutral.grey[2]};
  background-color: fff;
  
  :hover {
    color: ${(props) => props.theme.colors.neutral.grey[3]};
    border: 2px solid ${(props) => props.theme.colors.neutral.grey[2]};
  }
  
  span svg path:last-child {
    stroke: none;
    fill: ${(props) => props.theme.colors.neutral.grey[2]};
  }
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    max-width: 215px;
  }
`;

const StyledButton = styled(Button)`
  font-family: CurrysSans;
  padding-right: 29px;
  padding-left: 29px;
  span svg path:last-child {
    stroke: none;
    fill: ${(props) => props.theme.colors.primary.purple};
  }
  
  @media ${(props) => props.theme.breakpoints.mobile} {
    padding-right: 39px;
    padding-left: 39px;
  }
`;

const titleCase = (string) => (string ? string[0].toUpperCase() + string.slice(1).toLowerCase() : string);

export const AddToBasketButton = (props) => {

  const [isInProgress, setInProgress] = useState(false);
  const [isAdded, setAdded] = useState(false);

  const handleClick = async () => {
    setInProgress(true);
    props.onAddToBasket();
    try {
      const response = await addToBasket(
        props.storeUrl,
        props.fupid,
        1,
        props.storeId,
        props.deliveryType,
        props.psaWhenBoughtWithEnabled,
        props.productID
      );

      setInProgress(false);
      setAdded(true);
      props.onAddToBasketSuccess(props.fupid, response);
    } catch (e) {
      setInProgress(false);
      props.onAddToBasketFailure(props.fupid, e);
    }
  };

  // In progress
  if (isInProgress || props.isInProgress) {
    return (
      <EnvironmentThemeProvider>
        <InProgressButton positive disabled>
          <StyledIcon icon={'loading'} spin />
          {titleCase(props.inProgressLabel)}
        </InProgressButton>
      </EnvironmentThemeProvider>
    );
  }

  // Already added to basket
  if (isAdded || props.isAdded) {
    return (
      <EnvironmentThemeProvider>
        <AddedButton positive disabled>
          <StyledIcon icon={'tick'} />
          {props.addedLabel}
        </AddedButton>
      </EnvironmentThemeProvider>
    );
  }

  // Add to basket
  return (
    <EnvironmentThemeProvider>
      <StyledButton
        onClick={handleClick}
        positive
      >
        <StyledIcon icon={'basket'} />
        { props.label }
      </StyledButton>
    </EnvironmentThemeProvider>
  );
};

AddToBasketButton.propTypes = {
  fupid: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  storeId: PropTypes.number,
  deliveryType: PropTypes.oneOf(['RESERVE_AND_COLLECT', 'PAY_AND_COLLECT', 'PAY_AND_COLLECT_FROM_STORE_STOCK']),
  label: PropTypes.string.isRequired,
  isInProgress: PropTypes.bool,
  inProgressLabel: PropTypes.string,
  isAdded: PropTypes.bool,
  addedLabel: PropTypes.string,
  onAddToBasket: PropTypes.func,
  onAddToBasketSuccess: PropTypes.func,
  onAddToBasketFailure: PropTypes.func,
  psaWhenBoughtWithEnabled: PropTypes.bool.isRequired,
  productID: PropTypes.string.isRequired,
  storeUrl: PropTypes.string.isRequired
};

AddToBasketButton.defaultProps = {
  isInProgress: false,
  inProgressLabel: '',
  isAdded: false,
  addedLabel: '',
  onAddToBasket: () => {},
  onAddToBasketSuccess: () => {},
  onAddToBasketFailure: () => {},
  storeId: undefined,
  deliveryType: undefined
};
